import api, { pointcastApi } from '.';
import logger from '../utils/logger';
import store from '../redux/store';
import { ApiConfig } from '../types/common';
import {
  CompanyBillingPreferences,
  CompanyDetails,
  CompanyLocation,
  Notification,
  NotificationType,
  PersonalDetails,
  State,
  CreateUpdateCompanyAddress,
  UpdatePersonalDetails,
  UpdateUserNotificationDetails,
  UserDetails,
  CreateUpdateCompanyUser,
  UpdateCompanyDetails,
  UpdateMessageSettings,
  MessageTypeWithChannels,
} from '../types/profile';

export const getPersonalDetails = async (): Promise<PersonalDetails> => {
  try {
    const { data } = await api.get(`/user`);
    return data;
  } catch (error) {
    logger('error', 'at getPersonalDetails while fetching personal information details');
    throw new Error('error at getPersonalDetails while fetching personal information details');
  }
};

export const updatePersonalDetails = async (requestBody: UpdatePersonalDetails): Promise<null> => {
  try {
    const { data } = await api.patch(
      `/users/${store.getState().auth.authContext?.userId}`,
      requestBody,
    );
    return data;
  } catch (error) {
    logger('error', 'at updatePersonalDetails while updating personal information details');
    throw new Error('error at updatePersonalDetails while updating personal information details');
  }
};

export const getCompanyDetails = async (companyId: number): Promise<CompanyDetails> => {
  try {
    const { data } = await api.get(`/companies/${companyId}`);
    return data;
  } catch (error) {
    logger('error', 'at getCompanyDetails while fetching company information details');
    throw new Error('error at getCompanyDetails while fetching company information details');
  }
};

export const updateCompanyDetails = async (
  requestBody: UpdateCompanyDetails,
): Promise<CompanyDetails> => {
  try {
    const { data } = await api.patch(
      `/companies/${store.getState().auth.authContext?.companyId}`,
      requestBody,
    );
    return data;
  } catch (error) {
    logger('error', 'at updateCompanyDetails while updating company information details');
    throw new Error('error at updateCompanyDetails while updating company information details');
  }
};

export const getCompanyBillingPreferencesDetails = async (): Promise<CompanyBillingPreferences> => {
  try {
    const { data } = await api.get(
      `/billing-service/company-preferences/${store.getState().auth.authContext?.companyId}`,
    );
    return data;
  } catch (error) {
    logger(
      'error',
      'at getCompanyBillingPreferencesDetails while fetching company billing preferences details',
    );
    throw new Error(
      'error at getCompanyBillingPreferencesDetails while fetching company billing preferences details',
    );
  }
};

export const getCompanyUsers = async (): Promise<UserDetails[]> => {
  try {
    const { data } = await api.get(
      `/companies/${store.getState().auth.authContext?.companyId}/users`,
    );
    return data;
  } catch (error) {
    logger('error', 'at getCompanyUsers while fetching company users');
    throw new Error('error at getCompanyUsers while fetching company users');
  }
};

export const getCompanyUserDetails = async (
  userId: number,
  companyId?: number,
  fetch_basic_data?: boolean,
): Promise<UserDetails> => {
  try {
    let url = `/companies/${
      companyId ?? store.getState().auth.authContext?.companyId
    }/users/${userId}`;

    const params = new URLSearchParams();
    if (fetch_basic_data) {
      params.append('type', 'contact_details');
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const { data } = await api.get(url);
    return data;
  } catch (error) {
    logger('error', 'at getCompanyUserDetails while fetching company user details');
    throw new Error('error at getCompanyUserDetails while fetching company user details');
  }
};

export const getCompanyLocations = async (
  config?: ApiConfig,
  fetchJobsites?: boolean,
): Promise<CompanyLocation[]> => {
  try {
    let url = `/rsp/locations?company_id=${store.getState().auth.authContext
      ?.companyId}&order_by=location_id&order_direction=DESC`;

    if (config) {
      const { page, size } = config;
      if (size) url += `&size=${size}`;
      if (page) url += `&page=${page}`;
    }

    if (fetchJobsites) {
      url += `&jobsite=${fetchJobsites}`;
    }

    const { data } = await api.get(url);
    return data;
  } catch (error) {
    logger('error', 'at getCompanyLocations while fetching company locations');
    throw new Error('error at getCompanyLocations while fetching company locations');
  }
};

export const getCompanyLocationById = async (
  locationId: number | undefined,
): Promise<CompanyLocation> => {
  try {
    const { data } = await api.get(`/rsp/locations/${locationId as number}`);
    return data;
  } catch (error) {
    logger('error', 'at getCompanyLocationById while fetching company location');
    throw new Error('error at getCompanyLocationById while fetching company location');
  }
};

export const getNotificationTypes = async (): Promise<NotificationType[]> => {
  try {
    const { data } = await api.get(`/alerting/notification-types`);
    return data;
  } catch (error) {
    logger('error', 'at getNotificationTypes while fetching notification types');
    throw new Error('error at getNotificationTypes while fetching notification types');
  }
};

export const getUserNotificationDetails = async (): Promise<Notification[]> => {
  try {
    const { data } = await api.get(`/alerting/users/${store.getState().auth.authContext?.userId}`);
    return data;
  } catch (error) {
    logger('error', 'at getUserNotificationDetails while fetching user notification details');
    throw new Error('error at getUserNotificationDetails while fetching user notification details');
  }
};

export const updateUserNotificationDetails = async (
  requestBody: UpdateUserNotificationDetails,
): Promise<null> => {
  try {
    const { data } = await api.patch(
      `/alerting/users/${store.getState().auth.authContext?.userId}`,
      requestBody,
    );
    return data;
  } catch (error) {
    logger('error', 'at updateUserNotificationDetails while updating user notification details');
    throw new Error(
      'error at updateUserNotificationDetails while updating user notification details',
    );
  }
};

export const updateMessageSettings = async (
  userId: number,
  requestBody: UpdateMessageSettings,
): Promise<null> => {
  try {
    const { data } = await pointcastApi.patch(`/users/${userId}/message-settings`, requestBody);
    return data;
  } catch (error) {
    logger('error', 'at updateMessageSettings while updating message settings');
    throw new Error('error at updateMessageSettings while updating message settings');
  }
};

export const getAllMessageSettings = async (userId: number): Promise<MessageTypeWithChannels[]> => {
  try {
    const response = await pointcastApi.get(`/users/${userId}/message-settings`);
    return response.data;
  } catch (error) {
    logger('error', 'at getAllMessageSettings while fetching all message settings');
    throw error;
  }
};

export const updateCompanyAddress = async (
  locationId: number | undefined | null,
  requestBody: CreateUpdateCompanyAddress,
): Promise<null> => {
  try {
    const { data } = await api.patch(`/rsp/locations/${locationId as number}`, requestBody);
    return data;
  } catch (error) {
    logger('error', 'at updateCompanyAddress while updating company address');
    throw new Error('error at updateCompanyAddress while updating company address');
  }
};

export const updateCompanyUser = async (
  userId: number | undefined,
  requestBody: CreateUpdateCompanyUser,
): Promise<null> => {
  try {
    const { data } = await api.patch(
      `/companies/${store.getState().auth.authContext?.companyId}/users/${userId}`,
      requestBody,
    );
    return data;
  } catch (error) {
    logger('error', 'at updateCompanyUser while updating company user');
    throw new Error('error at updateCompanyUser while updating company user');
  }
};

export const deleteCompanyUser = async (userId: number | undefined): Promise<null> => {
  try {
    const { data } = await api.delete(
      `/companies/${store.getState().auth.authContext?.companyId}/users/${userId}`,
    );
    return data;
  } catch (error) {
    logger('error', 'at deleteCompanyUser while deleting company user');
    throw new Error('error at deleteCompanyUser while deleting company user');
  }
};

export const getStates = async (): Promise<State[]> => {
  try {
    const { data } = await api.get(`/states`);
    return data;
  } catch (error) {
    logger('error', 'at getStates while fetching states');
    throw new Error('error at getStates while fetching states');
  }
};

export const addCompanyAddress = async (
  requestBody: CreateUpdateCompanyAddress,
): Promise<CompanyLocation> => {
  try {
    const { data } = await api.post(
      `/companies/${store.getState().auth.authContext?.companyId}/locations`,
      requestBody,
    );
    return data;
  } catch (error) {
    logger('error', 'at addCompanyAddress while creating company address');
    throw new Error('error at addCompanyAddress while creating company address');
  }
};
